import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye, History, RefreshCcw } from 'lucide-react';
import { SetupRecord } from '../../types/deployment';
import { SetupRunHistoryState } from './SetupHistoryPanel';
import {
  SETUP_STATUS_COLORS,
  SETUP_STATUS_LABELS,
  formatDateTime,
  formatDryRunSummary,
  getStackRunPhases,
} from './setupExplorerUtils';

interface StackRunHistoryPanelProps {
  historyState?: SetupRunHistoryState;
  onLoadMore: () => void;
  onOpenRunDetails: (setup: SetupRecord) => void;
  onRefreshHistory: () => void;
  saving?: boolean;
}

export const StackRunHistoryPanel: React.FC<StackRunHistoryPanelProps> = ({
  historyState,
  onLoadMore,
  onOpenRunDetails,
  onRefreshHistory,
  saving = false,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mt: 1.5,
        ml: { md: 4 },
        bgcolor: 'rgba(255,255,255,0.02)',
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <History size={16} />
            <Typography variant="subtitle2">Historical Stack Runs</Typography>
            {historyState?.loaded ? (
              <Chip size="small" variant="outlined" label={`${historyState.items.length}/${historyState.total}`} />
            ) : null}
          </Stack>
          <Button
            size="small"
            variant="text"
            startIcon={historyState?.loading ? <CircularProgress size={14} /> : <RefreshCcw size={14} />}
            disabled={historyState?.loading || saving}
            onClick={onRefreshHistory}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        {historyState?.error ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {historyState.error}
          </Alert>
        ) : null}

        {historyState?.loading && !historyState.loaded ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : null}

        {historyState?.loaded && historyState.items.length === 0 && !historyState.loading ? (
          <Typography variant="body2" color="text.secondary">
            No older stack runs were found for this stack yet.
          </Typography>
        ) : null}

        <Stack spacing={1.25}>
          {(historyState?.items || []).map((stackRun) => {
            const phases = getStackRunPhases(stackRun);
            const dryRunSummary = formatDryRunSummary(stackRun.metadata?.dryRun?.summary);

            return (
              <Card key={stackRun._id} variant="outlined" sx={{ px: 1.5, py: 1.25 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1.5}
                  alignItems={{ md: 'center' }}
                >
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" fontWeight={600}>
                        {String(stackRun.metadata?.stackLabel || 'Stack run')}
                      </Typography>
                      {stackRun.actionType === 'stack-dry-run' ? (
                        <Chip size="small" variant="outlined" label="Stack Dry Run" />
                      ) : null}
                      <Chip
                        size="small"
                        color={SETUP_STATUS_COLORS[stackRun.status] || 'default'}
                        label={SETUP_STATUS_LABELS[stackRun.status] || stackRun.status}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {stackRun.currentStep || stackRun.failedStep || 'No active phase'} • Started {formatDateTime(stackRun.startedAt ?? stackRun.createdAt)}
                    </Typography>
                    {dryRunSummary ? (
                      <Typography variant="caption" color="text.secondary">
                        Dry run: {dryRunSummary}
                      </Typography>
                    ) : null}
                    {phases.slice(0, 2).map((phase) => (
                      <Typography key={`${stackRun._id}-${phase.index}`} variant="caption" color="text.secondary">
                        {phase.name}: {phase.status || 'pending'}
                        {phase.failureClass ? ` (${phase.failureClass})` : ''}
                      </Typography>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="View full details">
                      <span>
                        <IconButton size="small" onClick={() => onOpenRunDetails(stackRun)}>
                          <Eye size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>

        {historyState && historyState.items.length < historyState.total ? (
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onLoadMore}
              disabled={historyState.loading || saving}
            >
              Load More History
            </Button>
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};
